import type { ApiError, ApiResponse, HealthResponse, LoginResponse } from '@upweo/types';

/**
 * The single seam between this SPA and apps/api (Directive #4).
 *
 * TOKEN HANDLING
 * The access token lives in a module variable — deliberately not localStorage.
 * Anything in localStorage survives the tab and is readable by any script that
 * gets injected; an in-memory token dies with the page. Continuity across
 * reloads comes from the httpOnly refresh cookie instead, which JavaScript
 * cannot read at all.
 */

const API_ORIGIN = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');
const API_BASE = `${API_ORIGIN}/admin/api/v1`;

export class ApiClientError extends Error {
  readonly status: number;
  readonly error: ApiError;

  constructor(status: number, error: ApiError) {
    super(error.message);
    this.name = 'ApiClientError';
    this.status = status;
    this.error = error;
  }
}

let accessToken: string | null = null;
let activeShopSlug: string | null = null;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};
export const getAccessToken = (): string | null => accessToken;

/**
 * The store every scoped request is made against, sent as X-Upweo-Shop.
 *
 * A SLUG, not an id — that is what `resolveTenant` on the server looks up.
 * Held per tab so two tabs can be pointed at two different stores; the server
 * also remembers the last choice on the session, which is what makes a page
 * reload land back on the same store.
 */
export const setActiveShop = (slug: string | null): void => {
  activeShopSlug = slug;
};
export const getActiveShop = (): string | null => activeShopSlug;

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  readonly rootPath?: boolean;
  /** Internal: prevents a refresh loop. */
  readonly _retrying?: boolean;
}

const buildHeaders = (options: RequestOptions): Record<string, string> => {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }
  if (activeShopSlug) {
    headers['X-Upweo-Shop'] = activeShopSlug;
  }
  return headers;
};

/**
 * Ask the server for a fresh access token using the refresh cookie.
 *
 * Concurrent 401s share ONE in-flight refresh. Without this, a dashboard that
 * fires five queries on mount would send five refreshes, and since each
 * rotates the token, four would be rejected and log the user out.
 */
let refreshInFlight: Promise<boolean> | null = null;

const refreshAccessToken = async (): Promise<boolean> => {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        return false;
      }
      const payload = (await response.json()) as ApiResponse<LoginResponse>;
      if (!payload.ok) {
        return false;
      }
      accessToken = payload.data.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
};

export const apiRequest = async <TData>(
  path: string,
  options: RequestOptions = {},
): Promise<TData> => {
  const url = options.rootPath ? `${API_ORIGIN}${path}` : `${API_BASE}${path}`;

  const response = await fetch(url, {
    method: options.method ?? 'GET',
    headers: buildHeaders(options),
    credentials: 'include',
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  // A 15-minute access token WILL expire mid-session. Refresh once and replay
  // the request, so expiry is invisible rather than a spurious logout.
  if (response.status === 401 && !options._retrying && !path.startsWith('/auth/')) {
    if (await refreshAccessToken()) {
      return apiRequest<TData>(path, { ...options, _retrying: true });
    }
  }

  const payload = (await response.json()) as ApiResponse<TData>;

  if (!response.ok || !payload.ok) {
    const error: ApiError = payload.ok
      ? {
          code: 'INTERNAL_ERROR',
          message: `Request failed with status ${String(response.status)}`,
          requestId: response.headers.get('x-request-id') ?? 'unknown',
        }
      : payload.error;
    throw new ApiClientError(response.status, error);
  }

  return payload.data;
};

export const fetchHealth = (signal?: AbortSignal): Promise<HealthResponse> =>
  apiRequest<HealthResponse>('/health', { rootPath: true, ...(signal ? { signal } : {}) });

/** Restore a session on page load from the refresh cookie alone. */
export const restoreSession = async (): Promise<boolean> => refreshAccessToken();
